import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Trash2, Plus, Minus, Tag, ShoppingBag, Clock, Calendar } from "lucide-react";
import { useCart, API } from "@/App";
import { useLanguage } from "@/LanguageContext";
import { toast } from "sonner";

const OrderPage = () => {
  const navigate = useNavigate();
  const {
    cart,
    updateQuantity,
    removeFromCart,
    clearCart,
    cartTotal,
    giftCardCode,
    setGiftCardCode,
    giftCardDiscount,
    setGiftCardDiscount,
  } = useCart();
  const { t } = useLanguage();

  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    orderType: "pickup",
    paymentMethod: "online",
    notes: "",
    pickupType: "asap",
    pickupDate: "",
    pickupTime: "",
    marketing_consent: false,
  });
  const [giftCardInput, setGiftCardInput] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [validatingGiftCard, setValidatingGiftCard] = useState(false);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [restaurantSettings, setRestaurantSettings] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [tipOption, setTipOption] = useState(null); // null = not selected, "none", "5", "10", "15", "custom"
  const [customTip, setCustomTip] = useState("");
  const [showTipModal, setShowTipModal] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);

  // Check for Google user data and pre-fill form
  useEffect(() => {
    const userData = localStorage.getItem("google_user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setGoogleUser(user);
        setCustomerInfo(prev => ({
          ...prev,
          name: prev.name || user.name || "",
          email: prev.email || user.email || "",
        }));
      } catch (e) { /* ignore */ }
    }
  }, []);

  // Generate available dates (today and next 7 days)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      const dayName = date.toLocaleDateString('nb-NO', { weekday: 'short' });
      const dateStr = date.toISOString().split('T')[0];
      const displayDate = date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
      
      dates.push({
        value: dateStr,
        label: i === 0 ? `I dag (${displayDate})` : `${dayName} ${displayDate}`,
        isToday: i === 0
      });
    }
    return dates;
  };

  // Get restaurant closing hour for a given date (0=Sun, 1=Mon, ..., 6=Sat)
  const getClosingHour = (date) => {
    const day = date.getDay();
    // Fri(5) and Sat(6) close at 23:00, rest at 22:00
    return (day === 5 || day === 6) ? 23 : 22;
  };

  // Generate available times based on selected date
  // Restaurant opens 15:00, earliest pickup 15:30
  // Last order 30 min before closing
  const generateAvailableTimes = (selectedDate) => {
    const times = [];
    const now = new Date();
    const isToday = selectedDate === now.toISOString().split('T')[0];
    
    // Parse the selected date to get day of week
    const selDate = new Date(selectedDate + 'T00:00:00');
    const closeHour = getClosingHour(selDate);
    
    // Restaurant opens at 15:00, earliest pickup at 15:30
    const openHour = 15;
    const earliestPickupMinute = 30; // 15:30
    
    // Last order is 30 min before closing
    const lastOrderHour = closeHour;
    const lastOrderMinute = -30; // 30 min before closeHour
    // Calculate actual cutoff in minutes from midnight
    const cutoffMinutes = (closeHour * 60) - 30;
    
    let startHour = openHour;
    let startMinute = earliestPickupMinute;
    
    if (isToday) {
      // Minimum 30 minutes from now
      const minTime = new Date(now.getTime() + 30 * 60000);
      const minHour = minTime.getHours();
      const minMin = Math.ceil(minTime.getMinutes() / 15) * 15;
      
      let adjustedHour = minHour;
      let adjustedMin = minMin;
      if (adjustedMin >= 60) {
        adjustedHour++;
        adjustedMin = 0;
      }
      
      // Use whichever is later: earliest pickup (15:30) or 30 min from now
      const earliestMinutes = openHour * 60 + earliestPickupMinute;
      const nowMinutes = adjustedHour * 60 + adjustedMin;
      
      if (nowMinutes > earliestMinutes) {
        startHour = adjustedHour;
        startMinute = adjustedMin;
      }
    }
    
    // Generate 15-minute intervals up to 30 min before closing
    for (let h = startHour; h <= closeHour; h++) {
      const startM = (h === startHour) ? startMinute : 0;
      for (let m = startM; m < 60; m += 15) {
        const totalMin = h * 60 + m;
        if (totalMin > cutoffMinutes) break;
        const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        times.push(timeStr);
      }
    }
    
    return times;
  };

  useEffect(() => {
    fetchSettings();
    setAvailableDates(generateAvailableDates());
  }, []);

  // Update available times when date changes
  useEffect(() => {
    if (customerInfo.pickupDate) {
      const times = generateAvailableTimes(customerInfo.pickupDate);
      setAvailableTimes(times);
    }
  }, [customerInfo.pickupDate]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setRestaurantSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const baseTotal = cartTotal - giftCardDiscount - couponDiscount;
  const tipAmount = tipOption === "custom" 
    ? (parseFloat(customTip) || 0) 
    : (!tipOption || tipOption === "none") 
      ? 0 
      : Math.round(baseTotal * (parseInt(tipOption) / 100));
  const finalTotal = baseTotal + tipAmount;

  const validateCoupon = async () => {
    if (!couponInput.trim()) {
      toast.error("Vennligst skriv inn en kupongkode");
      return;
    }

    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, null, {
        params: {
          code: couponInput,
          order_total: cartTotal - giftCardDiscount,
          order_type: customerInfo.orderType,
        },
      });
      if (response.data.valid) {
        setAppliedCoupon(response.data.code);
        setCouponDiscount(response.data.discount);
        toast.success(`Kupong brukt! ${response.data.discount} NOK rabatt`);
      }
    } catch (error) {
      console.error("Coupon validation error:", error);
      toast.error(error.response?.data?.detail || "Ugyldig kupong");
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon("");
    setCouponDiscount(0);
    setCouponInput("");
    toast.info("Kupong fjernet");
  };

  const validateGiftCard = async () => {
    if (!giftCardInput.trim()) {
      toast.error("Vennligst skriv inn en gavekortkode");
      return;
    }

    setValidatingGiftCard(true);
    try {
      const response = await axios.post(`${API}/gift-cards/validate`, null, {
        params: { card_code: giftCardInput },
      });
      if (response.data.valid) {
        setGiftCardCode(giftCardInput);
        setGiftCardDiscount(Math.min(response.data.balance, cartTotal));
        toast.success(`Gavekort brukt! ${response.data.balance} NOK tilgjengelig`);
      }
    } catch (error) {
      console.error("Gift card validation error:", error);
      toast.error(error.response?.data?.detail || "Ugyldig gavekort");
    } finally {
      setValidatingGiftCard(false);
    }
  };

  const removeGiftCard = () => {
    setGiftCardCode("");
    setGiftCardDiscount(0);
    setGiftCardInput("");
    toast.info("Gavekort fjernet");
  };

  const handleSubmitOrder = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (cart.length === 0) {
      toast.error("Handlekurven din er tom");
      return;
    }

    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error("Vennligst fyll ut alle obligatoriske felt");
      return;
    }

    // Check busy mode
    if (restaurantSettings?.busy_mode_enabled) {
      const confirmed = window.confirm(restaurantSettings.busy_mode_message);
      if (!confirmed) {
        return;
      }
    }

    setSubmitting(true);
    try {
      // Format pickup time based on selection
      let formattedPickupTime = null;
      if (customerInfo.pickupType === "asap") {
        formattedPickupTime = "Så fort som mulig";
      } else if (customerInfo.pickupDate && customerInfo.pickupTime) {
        const dateLabel = availableDates.find(d => d.value === customerInfo.pickupDate)?.label || customerInfo.pickupDate;
        formattedPickupTime = `${dateLabel} kl. ${customerInfo.pickupTime}`;
      }

      const orderData = {
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        delivery_address: null,
        order_type: customerInfo.orderType,
        customer_notes: customerInfo.notes || null,
        pickup_time: formattedPickupTime,
        pickup_type: customerInfo.pickupType,
        pickup_date: customerInfo.pickupDate || null,
        scheduled_time: customerInfo.pickupTime || null,
        line_items: cart,
        gift_card_code: giftCardCode || null,
        coupon_code: appliedCoupon || null,
        payment_method: customerInfo.paymentMethod,
        tip_amount: tipAmount,
        marketing_consent: customerInfo.marketing_consent === true,
      };

      const response = await axios.post(`${API}/orders`, orderData);

      // If paying at restaurant, no payment URL needed
      if (customerInfo.paymentMethod === "at_restaurant") {
        clearCart();
        navigate(`/track/${response.data.order_number}`);
        toast.success("Bestilling sendt! Betal når du henter.");
      } else if (response.data.payment_url) {
        // Redirect to Nets payment for online payment
        window.location.href = response.data.payment_url;
      } else {
        // Order fully paid by gift card
        clearCart();
        navigate(`/track/${response.data.order_number}`);
        toast.success("Bestilling sendt!");
      }
    } catch (error) {
      console.error("Order submission error:", error);
      toast.error(error.response?.data?.detail || "Kunne ikke sende bestilling");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Header */}
      <div 
        className="py-16 mandala-pattern"
        style={{ 
          background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="indian-divider mb-4"
          >
            <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Bestilling</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-serif-display font-semibold italic mb-4"
            style={{ color: '#d4af37' }}
            data-testid="order-page-title"
          >
            Din Bestilling
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ color: '#f5e6d3' }}
          >
            {t("order_subtitle")}
          </motion.p>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Busy Mode Alert */}
        {restaurantSettings?.busy_mode_enabled && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 rounded-xl p-6 text-center"
            style={{ background: '#721111', border: '2px solid #d4af37' }}
            data-testid="busy-mode-alert"
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="text-3xl">⏰</span>
              <h3 className="text-xl font-bold" style={{ color: '#d4af37' }}>Restaurant er Travelt</h3>
            </div>
            <p style={{ color: '#f5e6d3' }}>{restaurantSettings.busy_mode_message}</p>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div 
              className="bg-white rounded-xl shadow-lg p-6"
              style={{ border: '2px solid #b27f47' }}
              data-testid="cart-items-section"
            >
              <h2 className="text-2xl font-serif-display font-semibold mb-6" style={{ color: '#2d1810' }}>
                <ShoppingBag className="inline-block mr-2 h-6 w-6" style={{ color: '#721111' }} />
                Handlekurv
              </h2>

              {cart.length === 0 ? (
                <div className="text-center py-12" data-testid="empty-cart">
                  <p className="mb-4" style={{ color: '#4a3728' }}>{t("order_empty")}</p>
                  <Button
                    onClick={() => navigate("/menu")}
                    className="rounded-full btn-maroon"
                  >
                    Se Meny
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div
                      key={item.cart_key}
                      className="flex items-center gap-4 p-4 rounded-xl"
                      style={{ background: '#f4e7d8', border: '1px solid #b27f47' }}
                      data-testid={`cart-item-${item.cart_key}`}
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold" style={{ color: '#2d1810' }} data-testid={`cart-item-name-${item.cart_key}`}>
                          {item.name}
                        </h3>
                        <p className="text-sm" style={{ color: '#4a3728' }}>kr {item.unit_price},- per stk</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(item.cart_key, item.quantity - 1)}
                          className="h-8 w-8 p-0 rounded-full"
                          style={{ borderColor: '#721111', color: '#721111' }}
                          data-testid={`decrease-quantity-${item.cart_key}`}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center font-semibold" style={{ color: '#2d1810' }} data-testid={`cart-item-quantity-${item.cart_key}`}>
                          {item.quantity}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(item.cart_key, item.quantity + 1)}
                          className="h-8 w-8 p-0 rounded-full"
                          style={{ borderColor: '#721111', color: '#721111' }}
                          data-testid={`increase-quantity-${item.cart_key}`}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="text-right">
                        <p className="font-bold" style={{ color: '#721111' }} data-testid={`cart-item-total-${item.cart_key}`}>
                          kr {item.total_price},-
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromCart(item.cart_key)}
                        className="hover:bg-red-50"
                        style={{ color: '#721111' }}
                        data-testid={`remove-item-${item.cart_key}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Customer Information Form */}
            {cart.length > 0 && (
              <div 
                className="bg-white rounded-xl shadow-lg p-6 mt-6"
                style={{ border: '2px solid #b27f47' }}
                data-testid="customer-info-section"
              >
                <h2 className="text-2xl font-serif-display font-semibold mb-6" style={{ color: '#2d1810' }}>
                  {t("order_customer_info")}
                </h2>

                {!googleUser && (
                  <button
                    type="button"
                    onClick={() => {
                      const currentUrl = window.location.origin + '/bestill';
                      window.location.href = `https://demobackend.emergentagent.com/auth/v1/env/oauth/google?redirect_url=${encodeURIComponent(currentUrl)}`;
                    }}
                    className="w-full rounded-full font-bold py-3 mb-4 flex items-center justify-center gap-3 transition-transform hover:scale-[1.02]"
                    style={{ background: '#fff', color: '#2d1810', border: '2px solid #e5e7eb' }}
                    data-testid="google-login-customer"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Fyll ut med Google
                  </button>
                )}

                {googleUser && (
                  <div className="flex items-center gap-3 p-3 rounded-lg mb-4" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <span className="text-sm" style={{ color: '#166534' }}>Logget inn som <strong>{googleUser.name}</strong></span>
                  </div>
                )}

                <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
                  <div>
                    <Label htmlFor="name" style={{ color: '#2d1810' }}>{t("order_name")} *</Label>
                    <Input
                      id="name"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                      required
                      className="indian-input mt-1"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" style={{ color: '#2d1810' }}>E-post *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                      required
                      className="indian-input mt-1"
                      data-testid="customer-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" style={{ color: '#2d1810' }}>Telefon *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                      required
                      className="indian-input mt-1"
                      data-testid="customer-phone-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>Betalingsmetode *</Label>
                    <RadioGroup
                      value={customerInfo.paymentMethod}
                      onValueChange={(value) => setCustomerInfo({ ...customerInfo, paymentMethod: value })}
                      className="space-y-3 mt-2"
                      data-testid="payment-method-radio-group"
                    >
                      <div 
                        className="flex items-center space-x-2 p-4 rounded-lg transition-colors cursor-pointer"
                        style={{ background: customerInfo.paymentMethod === 'online' ? '#f4e7d8' : 'white', border: '1px solid #b27f47' }}
                      >
                        <RadioGroupItem value="online" id="online" data-testid="payment-online" />
                        <Label htmlFor="online" className="cursor-pointer flex-1">
                          <div>
                            <div className="font-semibold" style={{ color: '#2d1810' }}>Betal Online Nå (Kort)</div>
                            <div className="text-xs" style={{ color: '#4a3728' }}>Sikker betaling via Nets - Betal med kreditt/debetkort</div>
                          </div>
                        </Label>
                      </div>
                      <div 
                        className="flex items-center space-x-2 p-4 rounded-lg transition-colors cursor-pointer"
                        style={{ background: customerInfo.paymentMethod === 'at_restaurant' ? '#f4e7d8' : 'white', border: '1px solid #b27f47' }}
                      >
                        <RadioGroupItem value="at_restaurant" id="at_restaurant" data-testid="payment-at-restaurant" />
                        <Label htmlFor="at_restaurant" className="cursor-pointer flex-1">
                          <div>
                            <div className="font-semibold" style={{ color: '#2d1810' }}>Betal i Restaurant (Kontant eller Kort)</div>
                            <div className="text-xs" style={{ color: '#4a3728' }}>Betal når du henter bestillingen</div>
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div>
                    <Label htmlFor="notes" style={{ color: '#2d1810' }}>Spesielle Instruksjoner (Valgfritt)</Label>
                    <Textarea
                      id="notes"
                      value={customerInfo.notes}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                      placeholder="F.eks. 'Ekstra sterk', 'Ingen løk', allergier"
                      rows={2}
                      className="indian-input mt-1"
                      data-testid="customer-notes-textarea"
                    />
                  </div>

                  {/* Pickup Time Selection */}
                  <div className="space-y-4">
                    <Label style={{ color: '#2d1810' }} className="flex items-center gap-2">
                      <Clock className="h-4 w-4" style={{ color: '#721111' }} />
                      Når vil du hente maten? *
                    </Label>
                    
                    {/* ASAP or Scheduled */}
                    <RadioGroup
                      value={customerInfo.pickupType}
                      onValueChange={(value) => setCustomerInfo({ 
                        ...customerInfo, 
                        pickupType: value,
                        pickupDate: value === "asap" ? "" : customerInfo.pickupDate,
                        pickupTime: value === "asap" ? "" : customerInfo.pickupTime
                      })}
                      className="space-y-3"
                      data-testid="pickup-type-radio"
                    >
                      <div 
                        className="flex items-center space-x-2 p-4 rounded-lg transition-colors cursor-pointer"
                        style={{ 
                          background: customerInfo.pickupType === 'asap' ? '#dcfce7' : 'white', 
                          border: customerInfo.pickupType === 'asap' ? '2px solid #22c55e' : '1px solid #b27f47' 
                        }}
                      >
                        <RadioGroupItem value="asap" id="asap" data-testid="pickup-asap" />
                        <Label htmlFor="asap" className="cursor-pointer flex-1">
                          <div>
                            <div className="font-semibold" style={{ color: '#166534' }}>Så fort som mulig</div>
                            <div className="text-xs" style={{ color: '#15803d' }}>Vi gir deg beskjed når maten er klar (ca. 20-40 min)</div>
                          </div>
                        </Label>
                      </div>
                      <div 
                        className="flex items-center space-x-2 p-4 rounded-lg transition-colors cursor-pointer"
                        style={{ 
                          background: customerInfo.pickupType === 'scheduled' ? '#f4e7d8' : 'white', 
                          border: customerInfo.pickupType === 'scheduled' ? '2px solid #721111' : '1px solid #b27f47' 
                        }}
                      >
                        <RadioGroupItem value="scheduled" id="scheduled" data-testid="pickup-scheduled" />
                        <Label htmlFor="scheduled" className="cursor-pointer flex-1">
                          <div>
                            <div className="font-semibold" style={{ color: '#2d1810' }}>Velg dato og tid</div>
                            <div className="text-xs" style={{ color: '#4a3728' }}>Bestill til en bestemt tid (minimum 30 min frem)</div>
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>

                    {/* Date and Time Selection (only if scheduled) */}
                    {customerInfo.pickupType === "scheduled" && (
                      <div className="space-y-4 p-4 rounded-lg" style={{ background: '#f4e7d8' }}>
                        {/* Date Selection */}
                        <div>
                          <Label className="flex items-center gap-2 mb-2" style={{ color: '#2d1810' }}>
                            <Calendar className="h-4 w-4" style={{ color: '#721111' }} />
                            Velg dato
                          </Label>
                          <div className="flex flex-wrap gap-2">
                            {availableDates.map((date) => (
                              <button
                                key={date.value}
                                type="button"
                                onClick={() => setCustomerInfo({ ...customerInfo, pickupDate: date.value })}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                  customerInfo.pickupDate === date.value 
                                    ? 'text-white shadow-md' 
                                    : 'bg-white hover:bg-gray-50'
                                }`}
                                style={customerInfo.pickupDate === date.value 
                                  ? { background: '#721111' } 
                                  : { border: '1px solid #b27f47', color: '#2d1810' }
                                }
                                data-testid={`pickup-date-${date.value}`}
                              >
                                {date.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Time Selection (only after date is selected) */}
                        {customerInfo.pickupDate && (
                          <div>
                            <Label className="flex items-center gap-2 mb-2" style={{ color: '#2d1810' }}>
                              <Clock className="h-4 w-4" style={{ color: '#721111' }} />
                              Velg tid
                            </Label>
                            {availableTimes.length === 0 ? (
                              <p className="text-sm" style={{ color: '#dc2626' }}>
                                Ingen tilgjengelige tider for denne datoen. Velg en annen dato.
                              </p>
                            ) : (
                              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                {availableTimes.map((time) => (
                                  <button
                                    key={time}
                                    type="button"
                                    onClick={() => setCustomerInfo({ ...customerInfo, pickupTime: time })}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                      customerInfo.pickupTime === time 
                                        ? 'text-white shadow-md' 
                                        : 'bg-white hover:bg-gray-50'
                                    }`}
                                    style={customerInfo.pickupTime === time 
                                      ? { background: '#721111' } 
                                      : { border: '1px solid #b27f47', color: '#2d1810' }
                                    }
                                    data-testid={`pickup-time-${time}`}
                                  >
                                    {time}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Selected Summary */}
                        {customerInfo.pickupDate && customerInfo.pickupTime && (
                          <div className="p-3 rounded-lg" style={{ background: '#dcfce7', border: '1px solid #22c55e' }}>
                            <p className="text-sm font-medium" style={{ color: '#166534' }}>
                              Valgt hentetid: {availableDates.find(d => d.value === customerInfo.pickupDate)?.label} kl. {customerInfo.pickupTime}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Order Summary */}
          {cart.length > 0 && (
            <div className="lg:col-span-1">
              <div 
                className="bg-white rounded-xl shadow-lg p-6 sticky top-24"
                style={{ border: '2px solid #d4af37' }}
                data-testid="order-summary"
              >
                <h2 className="text-2xl font-serif-display font-semibold mb-6" style={{ color: '#2d1810' }}>
                  Ordresammendrag
                </h2>

                {/* Coupon Code */}
                <div 
                  className="mb-4 p-4 rounded-xl"
                  style={{ background: '#f4e7d8', border: '1px solid #721111' }}
                  data-testid="coupon-section"
                >
                  <Label className="flex items-center gap-2 mb-2" style={{ color: '#2d1810' }}>
                    <Tag className="h-4 w-4" style={{ color: '#721111' }} />
                    Har du en kupongkode?
                  </Label>
                  {!appliedCoupon ? (
                    <div className="flex gap-2">
                      <Input
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder="HENT15"
                        className="bg-white"
                        data-testid="coupon-input"
                      />
                      <Button
                        onClick={validateCoupon}
                        disabled={validatingCoupon}
                        size="sm"
                        className="btn-maroon"
                        data-testid="apply-coupon-button"
                      >
                        {validatingCoupon ? "..." : "Bruk"}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold" style={{ color: '#006400' }} data-testid="applied-coupon-code">
                        {appliedCoupon} brukt
                      </span>
                      <Button
                        onClick={removeCoupon}
                        size="sm"
                        variant="ghost"
                        style={{ color: '#721111' }}
                        data-testid="remove-coupon-button"
                      >
                        Fjern
                      </Button>
                    </div>
                  )}
                </div>

                {/* Gift Card */}
                <div 
                  className="mb-6 p-4 rounded-xl"
                  style={{ background: '#f4e7d8', border: '1px solid #d4af37' }}
                  data-testid="gift-card-section"
                >
                  <Label className="flex items-center gap-2 mb-2" style={{ color: '#2d1810' }}>
                    <Tag className="h-4 w-4" style={{ color: '#d4af37' }} />
                    Har du et gavekort?
                  </Label>
                  {!giftCardCode ? (
                    <div className="flex gap-2">
                      <Input
                        value={giftCardInput}
                        onChange={(e) => setGiftCardInput(e.target.value)}
                        placeholder="Skriv inn kode"
                        className="bg-white"
                        data-testid="gift-card-input"
                      />
                      <Button
                        onClick={validateGiftCard}
                        disabled={validatingGiftCard}
                        size="sm"
                        className="btn-gold"
                        data-testid="apply-gift-card-button"
                      >
                        {validatingGiftCard ? "..." : "Bruk"}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold" style={{ color: '#006400' }} data-testid="applied-gift-card-code">
                        {giftCardCode} brukt
                      </span>
                      <Button
                        onClick={removeGiftCard}
                        size="sm"
                        variant="ghost"
                        style={{ color: '#721111' }}
                        data-testid="remove-gift-card-button"
                      >
                        Fjern
                      </Button>
                    </div>
                  )}
                </div>

                {/* Tips Summary */}
                <div className="mb-6 p-4 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #d4af37' }}>
                  {tipAmount > 0 ? (
                    <p className="text-sm font-semibold" style={{ color: '#721111' }}>
                      Tips: kr {tipAmount},- 
                      <button 
                        type="button" 
                        onClick={() => setShowTipModal(true)} 
                        className="ml-2 underline text-xs" style={{ color: '#8b4513' }}
                      >
                        Endre
                      </button>
                    </p>
                  ) : null}
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between" style={{ color: '#4a3728' }}>
                    <span>Delsum</span>
                    <span data-testid="subtotal-amount">kr {cartTotal.toFixed(2)},-</span>
                  </div>
                  {couponDiscount > 0 && (
                    <div className="flex justify-between font-semibold" style={{ color: '#721111' }}>
                      <span>Kupongrabatt</span>
                      <span data-testid="coupon-discount">-kr {couponDiscount.toFixed(2)},-</span>
                    </div>
                  )}
                  {giftCardDiscount > 0 && (
                    <div className="flex justify-between font-semibold" style={{ color: '#006400' }}>
                      <span>Gavekortrabatt</span>
                      <span data-testid="gift-card-discount">-kr {giftCardDiscount.toFixed(2)},-</span>
                    </div>
                  )}
                  {tipAmount > 0 && (
                    <div className="flex justify-between font-semibold" style={{ color: '#8b4513' }}>
                      <span>Tips</span>
                      <span data-testid="tip-amount">kr {tipAmount.toFixed(2)},-</span>
                    </div>
                  )}
                  <div 
                    className="pt-3 flex justify-between text-xl font-bold"
                    style={{ borderTop: '2px solid #d4af37', color: '#2d1810' }}
                  >
                    <span>Totalt</span>
                    <span style={{ color: '#721111' }} data-testid="final-total">
                      kr {finalTotal.toFixed(2)},-
                    </span>
                  </div>
                </div>

                {/* Marketing consent */}
                <div className="p-4 rounded-xl mt-1 mb-4" style={{ background: '#fdf8f0', border: '1px solid #d4af37' }}>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="marketing-consent-checkbox">
                    <input
                      type="checkbox"
                      checked={customerInfo.marketing_consent === true}
                      onChange={(e) => setCustomerInfo({...customerInfo, marketing_consent: e.target.checked})}
                      className="w-5 h-5 rounded accent-green-700"
                    />
                    <span className="font-semibold text-sm" style={{ color: '#2d1810' }}>
                      Ja, jeg vil gjerne motta tilbud og rabatter pa e-post
                    </span>
                  </label>
                  <p className="text-sm mt-2 ml-8" style={{ color: '#8b7355' }}>
                    Du kan melde deg av nar som helst
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (cart.length === 0) { toast.error("Handlekurven din er tom"); return; }
                    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) { toast.error("Vennligst fyll ut alle obligatoriske felt"); return; }
                    setShowTipModal(true);
                  }}
                  disabled={submitting}
                  className="w-full rounded-full font-bold py-6 btn-maroon transition-transform hover:scale-105 active:scale-95 shadow-lg"
                  data-testid="checkout-button"
                >
                  {submitting ? t("order_placing") : t("order_place")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* Tip Modal */}
      {showTipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="tip-modal">
          <div 
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            style={{ border: '3px solid #d4af37' }}
          >
            <h3 className="text-xl font-serif-display font-bold text-center mb-2" style={{ color: '#2d1810' }}>
              Vil du gi tips?
            </h3>
            <div className="mb-4"></div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              {[
                { value: "5", label: "5%", amount: Math.round(baseTotal * 0.05) },
                { value: "10", label: "10%", amount: Math.round(baseTotal * 0.10) },
                { value: "15", label: "15%", amount: Math.round(baseTotal * 0.15) },
                { value: "none", label: "Nei takk", amount: null },
                { value: "custom", label: "Annet", amount: null },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setTipOption(opt.value); if (opt.value !== "custom") setCustomTip(""); }}
                  className={`px-4 py-4 rounded-xl text-center font-semibold transition-all ${
                    tipOption === opt.value ? 'text-white shadow-lg scale-105' : 'hover:opacity-80'
                  }`}
                  style={{
                    background: tipOption === opt.value ? '#721111' : '#fdf8f0',
                    color: tipOption === opt.value ? '#d4af37' : '#4a3728',
                    border: `2px solid ${tipOption === opt.value ? '#721111' : '#d4af37'}`,
                  }}
                  data-testid={`tip-option-${opt.value}`}
                >
                  <span className="text-lg">{opt.label}</span>
                  {opt.amount !== null && (
                    <span className="block text-xs mt-1 opacity-75">kr {opt.amount},-</span>
                  )}
                </button>
              ))}
            </div>

            {tipOption === "custom" && (
              <div className="flex items-center gap-2 mb-4 justify-center">
                <span className="text-sm font-semibold" style={{ color: '#4a3728' }}>kr</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Skriv inn belop"
                  value={customTip}
                  onChange={(e) => setCustomTip(e.target.value)}
                  className="w-32 text-center"
                  style={{ borderColor: '#d4af37' }}
                  data-testid="custom-tip-input"
                  autoFocus
                />
              </div>
            )}

            {tipAmount > 0 && (
              <div className="text-center mb-4 p-3 rounded-lg" style={{ background: '#f4e7d8' }}>
                <p className="text-lg font-bold" style={{ color: '#721111' }}>
                  Total med tips: kr {finalTotal},-
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => {
                  if (!tipOption) {
                    toast.error("Velg et alternativ");
                    return;
                  }
                  if (tipOption === "none") {
                    setTipOption("none");
                    setCustomTip("");
                  }
                  setShowTipModal(false);
                  handleSubmitOrder();
                }}
                className="w-full rounded-full py-4 font-bold btn-maroon"
                data-testid="confirm-tip-btn"
              >
                {tipOption === "none" || !tipOption ? 'Legg inn Bestilling' : tipAmount > 0 ? `Betal kr ${finalTotal},-` : 'Bekreft'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderPage;
